import { RpmLeaderboardEntry } from '@/src/domain/models/TrainingLaunch';
import { useTranslation } from '@/src/i18n';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  ActivityIndicator,
  FlatList,
  Image,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

export type TabType = 'score' | 'rpm' | 'consistency' | 'launches';

interface TabConfig {
  key: TabType;
  labelKey: string;
  icon: React.ComponentProps<typeof MaterialCommunityIcons>['name'];
  getValue: (e: RpmLeaderboardEntry) => number;
  format: (v: number) => string;
  unit: string;
}

const TABS: TabConfig[] = [
  {
    key: 'score',
    labelKey: 'training.tabScore',
    icon: 'star-outline',
    getValue: (e) => e.avgScore,
    format: (v) => Math.round(v).toString(),
    unit: 'pts',
  },
  {
    key: 'rpm',
    labelKey: 'training.tabRpm',
    icon: 'speedometer',
    getValue: (e) => e.bestRpm,
    format: (v) => Math.round(v).toString(),
    unit: 'RPM',
  },
  {
    key: 'consistency',
    labelKey: 'training.tabConsistency',
    icon: 'target',
    getValue: (e) => e.avgConsistency,
    format: (v) => Math.round(v).toString(),
    unit: '%',
  },
  {
    key: 'launches',
    labelKey: 'training.tabLaunches',
    icon: 'rocket-launch-outline',
    getValue: (e) => e.totalLaunches,
    format: (v) => v.toString(),
    unit: '',
  },
];

interface LeaderboardRowProps {
  entry: RpmLeaderboardEntry;
  rank: number;
  isCurrentUser: boolean;
  tab: TabConfig;
  onPress: (userId: string) => void;
  colors: Colors;
}

function LeaderboardRow({ entry, rank, isCurrentUser, tab, onPress, colors }: LeaderboardRowProps) {
  const { t } = useTranslation();
  const rankColor = rank === 1 ? '#FFD700' : rank === 2 ? '#C0C0C0' : rank === 3 ? '#CD7F32' : colors.textSecondary;
  const primaryValue = tab.format(tab.getValue(entry));

  return (
    <TouchableOpacity
      style={[
        styles.row,
        { backgroundColor: isCurrentUser ? colors.primarySubtle : colors.card, borderColor: colors.border },
      ]}
      onPress={() => onPress(entry.userId)}
      activeOpacity={0.7}
    >
      <Text style={[styles.rank, { color: rankColor, fontWeight: rank <= 3 ? '700' : '500' }]}>
        {rank <= 3 ? ['🥇', '🥈', '🥉'][rank - 1] : `#${rank}`}
      </Text>

      {entry.avatarUrl ? (
        <Image source={{ uri: entry.avatarUrl }} style={styles.avatar} />
      ) : (
        <View style={[styles.avatar, styles.avatarPlaceholder, { backgroundColor: colors.primary }]}>
          <Text style={styles.avatarInitial}>{entry.displayName.slice(0, 1).toUpperCase()}</Text>
        </View>
      )}

      <View style={styles.nameContainer}>
        <Text style={[styles.name, { color: colors.text }]} numberOfLines={1}>
          {entry.displayName}
        </Text>
        <Text style={[styles.meta, { color: colors.textSecondary }]}>
          {t('training.launchMeta', { launches: entry.totalLaunches, consistency: Math.round(entry.avgConsistency) })}
        </Text>
      </View>

      <View style={styles.primaryMetric}>
        <MaterialCommunityIcons name={tab.icon} size={14} color={colors.primary} />
        <Text style={[styles.primaryValue, { color: colors.primary }]}>{primaryValue}</Text>
        {tab.unit ? <Text style={[styles.primaryUnit, { color: colors.textSecondary }]}>{tab.unit}</Text> : null}
      </View>
    </TouchableOpacity>
  );
}

export interface LeaderboardScreenColors {
  background: string;
  card: string;
  border: string;
  text: string;
  textSecondary: string;
  primary: string;
  primarySubtle: string;
  tabActive: string;
  tabInactive: string;
}

type Colors = LeaderboardScreenColors;

export interface LeaderboardScreenProps {
  entries: RpmLeaderboardEntry[];
  loading: boolean;
  refreshing: boolean;
  currentUserId?: string;
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  onRefresh: () => void;
  onUserPress: (userId: string) => void;
  colors: Colors;
}

export function LeaderboardScreen({
  entries,
  loading,
  refreshing,
  currentUserId,
  activeTab,
  onTabChange,
  onRefresh,
  onUserPress,
  colors,
}: LeaderboardScreenProps) {
  const { t } = useTranslation();

  const tab = TABS.find((tb) => tb.key === activeTab)!;

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Tab strip — always visible even during loading */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={[styles.tabBar, { borderBottomColor: colors.border }]}
        contentContainerStyle={styles.tabBarContent}
      >
        {TABS.map((tb) => {
          const isActive = tb.key === activeTab;
          return (
            <TouchableOpacity
              key={tb.key}
              style={[styles.tab, isActive && { borderBottomColor: colors.primary, borderBottomWidth: 2 }]}
              onPress={() => onTabChange(tb.key)}
              activeOpacity={0.7}
            >
              <MaterialCommunityIcons
                name={tb.icon}
                size={16}
                color={isActive ? colors.primary : colors.tabInactive}
              />
              <Text style={[styles.tabLabel, { color: isActive ? colors.primary : colors.tabInactive }]}>
                {t(tb.labelKey as any)}
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {loading ? (
        <View style={[styles.center, { backgroundColor: colors.background }]}>
          <ActivityIndicator color={colors.primary} size="large" />
        </View>
      ) : entries.length === 0 ? (
        <View style={[styles.center, { backgroundColor: colors.background }]}>
          <MaterialCommunityIcons name="rotate-right" size={48} color={colors.textSecondary} />
          <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
            {t('training.leaderboardEmpty')}
          </Text>
        </View>
      ) : (
        <FlatList
          data={entries}
          keyExtractor={(item) => item.userId}
          style={{ backgroundColor: colors.background }}
          contentContainerStyle={styles.list}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />}
          renderItem={({ item, index }) => (
            <LeaderboardRow
              entry={item}
              rank={index + 1}
              isCurrentUser={item.userId === currentUserId}
              tab={tab}
              onPress={onUserPress}
              colors={colors}
            />
          )}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    padding: 32,
  },
  emptyText: {
    textAlign: 'center',
    fontSize: 15,
    lineHeight: 22,
  },
  tabBar: {
    borderBottomWidth: 1,
    flexGrow: 0,
  },
  tabBarContent: {
    flexDirection: 'row',
    paddingHorizontal: 16,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabLabel: {
    fontSize: 13,
    fontWeight: '600',
  },
  list: {
    padding: 16,
    gap: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
    padding: 12,
    gap: 10,
  },
  rank: {
    width: 36,
    fontSize: 15,
    textAlign: 'center',
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  avatarPlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarInitial: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  nameContainer: {
    flex: 1,
    gap: 2,
  },
  name: {
    fontSize: 15,
    fontWeight: '600',
  },
  meta: {
    fontSize: 12,
  },
  primaryMetric: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  primaryValue: {
    fontSize: 16,
    fontWeight: '700',
  },
  primaryUnit: {
    fontSize: 11,
  },
});
